import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { payItemApi, F932 } from '../../../services/payItemApi';

export type Mode = 'browse' | 'add' | 'edit';

export function usePayItemForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F932[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [catCode, setCatCode] = useState('');
  const [isTax, setIsTax] = useState(false);
  const [attr, setAttr] = useState(1);
  const [memo, setMemo] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await payItemApi.list(1, 100); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  const syncFields = useCallback((r: F932) => {
    setCode(r.f932_01); setName(r.f932_11); setCatCode(r.f932_12);
    setIsTax(r.f932_13); setAttr(r.f932_14); setMemo(r.f932_18);
    setStartDate(r.f932_21 ? dayjs(r.f932_21) : null);
    setEndDate(r.f932_22 ? dayjs(r.f932_22) : null);
  }, []);

  useEffect(() => { if (current && mode === 'browse') syncFields(current); }, [current, mode, syncFields]);
  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setCode(''); setName(''); setCatCode(''); setIsTax(false); setAttr(1); setMemo(''); setStartDate(null); setEndDate(null); };
  const startEdit = () => { if (!current) return; setMode('edit'); syncFields(current); };
  const cancel = () => { setMode('browse'); if (current) syncFields(current); };

  const save = async () => {
    try {
      if (mode === 'add') {
        await payItemApi.create({ f932_01: code, f932_11: name, f932_12: catCode, f932_13: isTax, f932_14: attr, f932_18: memo, f932_21: startDate ? startDate.toISOString() : null, f932_22: endDate ? endDate.toISOString() : null });
        message.success('新增成功');
      } else {
        await payItemApi.update(current!.f932_01, { f932_11: name, f932_12: catCode, f932_13: isTax, f932_14: attr, f932_18: memo, f932_21: startDate ? startDate.toISOString() : null, f932_22: endDate ? endDate.toISOString() : null });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.f932_01;
      const idx = data.findIndex(r => r.f932_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await payItemApi.delete(current.f932_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const exact = records.findIndex(r => r.f932_01 === t);
    if (exact >= 0) { setCurrentIdx(exact); return; }
    const near = records.findIndex(r => r.f932_01 >= t);
    if (near >= 0) { setCurrentIdx(near); message.info(`定位至最近的「${records[near].f932_01}」`); }
    else { message.warning('查無此代號'); }
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    code, setCode, name, setName, catCode, setCatCode, isTax, setIsTax, attr, setAttr, memo, setMemo,
    startDate, setStartDate, endDate, setEndDate,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
